import React, { useContext } from "react"
import { Button, Div, Text } from "atomize"
import { DateTime } from "luxon"
import { motion } from "framer-motion"
import Container from "../atoms/Container"
import Divider from "../atoms/Divider"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { sharedDataContext } from "../providers/sharedData.provider"
import { canEditLesson, getLessonTypeTileColors } from "../utils/func.utils"
import { getSchoolRates, partecipantsWithoutPaymentMethod } from "../utils/billing.utils"
import { periodLabel } from "./LessonTypeLessonsModal"

const euros = amount => `${Math.round((Number(amount) || 0) * 100) / 100}€`

// Una lezione dell'elenco: quando era e chi non ha scritto come paga, con
// quanto. Si tocca e si apre la modifica, dove il metodo si sceglie.
function MissingPaymentRow({ lessonData, players, color, onClick }) {
	const start = lessonData.lessonStart?.toDate
		? DateTime.fromJSDate(lessonData.lessonStart.toDate())
		: null
	const end = lessonData.lessonEnd?.toDate
		? DateTime.fromJSDate(lessonData.lessonEnd.toDate())
		: null
	const total = players.reduce((sum, player) => sum + (Number(player.paymentAmount) || 0), 0)

	return (
		<motion.div whileTap={onClick && { scale: 0.98 }}>
			<Div
				p={{ y: "0.6rem" }}
				style={{ gap: "0.75rem", cursor: onClick ? "pointer" : "default" }}
				d="flex"
				align="center"
				onClick={onClick}>
				<Div w="0.5rem" h="2.5rem" rounded="circle" bg={color} style={{ flexShrink: 0 }} />
				<Div style={{ flex: 1, minWidth: 0 }}>
					<Text textColor={theme.colors.gunmetal} textWeight="bold">
						{start ? start.setLocale("it").toFormat("ccc d LLL") : "Data non segnata"}
						{start && end && (
							<Text tag="span" textWeight="normal" textColor={theme.colors.text_light}>
								{" · "}
								{start.toFormat("HH:mm")} - {end.toFormat("HH:mm")}
							</Text>
						)}
					</Text>
					{players.map((player, i) => (
						<Text key={player.playerID || i} textSize="caption" textColor={theme.colors.red}>
							{player.playerName?.trim()} · come paga i {euros(player.paymentAmount)}?
						</Text>
					))}
				</Div>
				<Text textColor={theme.colors.gunmetal} textWeight="bold" style={{ flexShrink: 0 }}>
					{euros(total)}
				</Text>
			</Div>
		</motion.div>
	)
}

// Le lezioni del periodo in cui qualcuno non ha il metodo di pagamento: sono
// quelle che nei pagamenti finiscono sotto "Non specificato". Toccandone una
// si apre la modifica; sistemata l'ultima, la riga nel profilo sparisce.
function MissingPaymentMethodModal({ closeModal, selection }) {
	const Modal = useContext(modalContext)
	const { coach } = useContext(profileContext)
	const { selectedSchool } = useContext(schoolContext)
	const { getLessonType } = useContext(sharedDataContext)

	const rates = getSchoolRates(coach?.billingData, selectedSchool)
	const lessons = selection?.lessons ?? []

	const handleLessonClick = lessonData =>
		canEditLesson(coach, selectedSchool, lessonData)
			? () => Modal.open("editLesson", lessonData)
			: undefined

	return (
		<>
			<Container>
				<Text textWeight="bold" textSize="title">
					Senza metodo di pagamento
				</Text>
				<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.5rem" }}>
					{[
						periodLabel(selection?.summaryDate),
						`${lessons.length} ${lessons.length === 1 ? "lezione" : "lezioni"}`,
					]
						.filter(Boolean)
						.join(" · ")}
				</Text>
			</Container>

			<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container>
					{lessons.length === 0 ? (
						<Text textColor={theme.colors.text_light} p={{ y: "0.5rem" }}>
							Tutte le lezioni hanno il metodo di pagamento
						</Text>
					) : (
						lessons.map((lesson, i) => (
							<Div key={lesson.lessonID ?? i}>
								<MissingPaymentRow
									lessonData={lesson}
									players={partecipantsWithoutPaymentMethod(lesson, rates)}
									color={getLessonTypeTileColors(getLessonType(lesson.lessonType)).bg}
									onClick={handleLessonClick(lesson)}
								/>
								{i < lessons.length - 1 && <Divider w="2px solid" />}
							</Div>
						))
					)}
				</Container>
			</Div>

			<Container>
				<Div d="flex" justify="center" m={{ t: "1rem" }}>
					<Button
						h="3.5rem"
						p={{ x: "2rem" }}
						bg={theme.colors.gunmetal_fill}
						textColor={theme.colors.white}
						textSize="subheader"
						rounded={theme.rounded["20px"]}
						onClick={closeModal}>
						Chiudi
					</Button>
				</Div>
			</Container>
		</>
	)
}

export default MissingPaymentMethodModal
